import React, { useState, useRef, useEffect } from "react"
import { Link } from "react-router-dom"
import { Container, Row, Col, Card, CardBody, Label, Form, Alert, Input, FormFeedback } from "reactstrap"
import { GoogleLogin } from "@react-oauth/google"
import logoDark from "../../../assets/images/pdflogo.png"
import logoLight from "../../../assets/images/pdflogo.png"
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"
import PropTypes from "prop-types"

import * as Yup from "yup"
import { useFormik } from "formik"
import withRouter from "components/Common/withRouter"

import { loginSuccess, apiError } from "store/actions"
import { loginApi, verifyLoginOtpApi, postSocialLogin, RequiresRoleSelectionError, getPostLoginRedirectPath } from "helpers/auth_api"

type LoginStep = "email_password" | "otp"

const Login = (props: any) => {
  document.title = "Login | SoldX"

  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState<LoginStep>("email_password")
  const [savedEmail, setSavedEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [otpError, setOtpError] = useState("")
  const [googleLoginType, setGoogleLoginType] = useState<"Buyer" | "Seller" | "Broker">("Buyer")
  const [showGoogleTypeStep, setShowGoogleTypeStep] = useState(false)
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState<string | null>(null)
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (step === "otp") {
      otpInputRefs.current[0]?.focus()
    }
  }, [step])

  const setAuthUserAndRedirect = (res: { data: any; token: string }) => {
    const d = res.data
    const authUser = {
      ...d,
      uid: d._id,
      username: d.fullName,
      accessToken: res.token,
      token: res.token,
      profileImageUrl:
        d.profileImageUrl ??
        d.profile_image_url ??
        d.avatar ??
        d.profileImage ??
        (d.profile && (d.profile.profileImageUrl ?? d.profile.avatar ?? d.profile.imageUrl)),
    }
    localStorage.setItem("authUser", JSON.stringify(authUser))
    dispatch(loginSuccess(authUser))
    props.router.navigate(getPostLoginRedirectPath(d.role))
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email").email("Invalid email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: async (values: { email: string; password: string }) => {
      if (step === "email_password") {
        setLoading(true)
        setOtpError("")
        try {
          const res = await loginApi(values)
          if (res.success && res.requiresOtp) {
            setSavedEmail(values.email)
            setStep("otp")
          } else if (res.success && res.data && res.token) {
            setAuthUserAndRedirect({ data: res.data, token: res.token })
          } else {
            dispatch(apiError(res.message || "Login failed"))
          }
        } catch (err: any) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Invalid email or password"
          dispatch(apiError(typeof msg === "string" ? msg : "Invalid email or password"))
        } finally {
          setLoading(false)
        }
        return
      }
      // step === "otp" – handled by OTP form submit
    },
  })

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setOtpError("")
    if (!otp.trim()) {
      setOtpError("Please enter the OTP sent to your email")
      return
    }
    setLoading(true)
    try {
      const res = await verifyLoginOtpApi({ email: savedEmail, otp: otp.trim() })
      if (res.success && res.data && res.token) {
        setAuthUserAndRedirect(res)
      } else {
        setOtpError(res.message || "Invalid OTP")
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Invalid or expired OTP"
      setOtpError(typeof msg === "string" ? msg : "Invalid or expired OTP")
    } finally {
      setLoading(false)
    }
  }

  const handleBackToEmailPassword = () => {
    setStep("email_password")
    setSavedEmail("")
    setOtp("")
    setOtpError("")
  }

  const OTP_LENGTH = 6
  const otpDigits = otp.padEnd(OTP_LENGTH, " ").split("").slice(0, OTP_LENGTH)

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1)
    const next = otp.slice(0, index) + digit + otp.slice(index + 1)
    const newOtp = next.slice(0, OTP_LENGTH).replace(/\D/g, "")
    setOtp(newOtp)
    setOtpError("")
    if (digit && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
      setOtp(otp.slice(0, index - 1))
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH)
    if (pasted.length > 0) {
      setOtp(pasted)
      setOtpError("")
      const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1)
      otpInputRefs.current[nextIndex]?.focus()
    }
  }

  const selectLoginState = (state: any) => state.Login
  const LoginProperties = createSelector(selectLoginState, (login) => ({
    error: login.error,
  }))
  const { error } = useSelector(LoginProperties)

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) return
    const credential = credentialResponse.credential
    setLoading(true)
    try {
      const user = await postSocialLogin({ credential })
      localStorage.setItem("authUser", JSON.stringify(user))
      dispatch(loginSuccess(user))
      props.router.navigate(getPostLoginRedirectPath(user.role))
    } catch (err) {
      if (err instanceof RequiresRoleSelectionError) {
        setPendingGoogleCredential(credential)
        setShowGoogleTypeStep(true)
      } else {
        console.error("Google login error:", err)
        dispatch(apiError(err instanceof Error ? err.message : "Google login failed"))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleRoleContinue = async () => {
    if (!pendingGoogleCredential) return
    setLoading(true)
    try {
      const user = await postSocialLogin({
        credential: pendingGoogleCredential,
        type: googleLoginType,
      })
      localStorage.setItem("authUser", JSON.stringify(user))
      dispatch(loginSuccess(user))
      props.router.navigate(getPostLoginRedirectPath(user.role))
    } catch (err) {
      console.error("Google login error:", err)
      dispatch(apiError(err instanceof Error ? err.message : "Google login failed"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <CardBody className="pt-0">
                  <h3 className="text-center mt-5 mb-4">
                    <Link to="/" className="d-block auth-logo">
                      <img src={logoDark} alt="" height="30" className="auth-logo-dark" />
                      <img src={logoLight} alt="" height="30" className="auth-logo-light" />
                    </Link>
                  </h3>

                  <div className="p-3">
                    {step === "email_password" ? (
                      <>
                        <h4 className="text-muted font-size-18 mb-1 text-center">Welcome Back!</h4>
                        <p className="text-muted text-center">Sign in to continue to SoldX.</p>
                        <Form
                          className="form-horizontal mt-4"
                          onSubmit={(e) => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                          }}
                        >
                          {error ? <Alert color="danger">{error}</Alert> : null}
                          <div className="mb-3">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              name="email"
                              className="form-control"
                              placeholder="Enter email"
                              type="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              invalid={!!(validation.touched.email && validation.errors.email)}
                            />
                            {validation.touched.email && validation.errors.email ? (
                              <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3">
                            <Label htmlFor="userpassword">Password</Label>
                            <Input
                              name="password"
                              value={validation.values.password || ""}
                              type="password"
                              placeholder="Enter Password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={!!(validation.touched.password && validation.errors.password)}
                            />
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                          </div>
                          <Row className="mb-3 mt-4">
                            <div className="col-6">
                              <div className="form-check">
                                <input type="checkbox" className="form-check-input" id="customControlInline" />
                                <label className="form-check-label" htmlFor="customControlInline">
                                  Remember me
                                </label>
                              </div>
                            </div>
                            <div className="col-6 text-end">
                              <button
                                className="btn btn-primary w-md waves-effect waves-light"
                                type="submit"
                                disabled={loading}
                              >
                                {loading ? "Sending OTP..." : "Log In"}
                              </button>
                            </div>
                          </Row>

                          <div className="position-relative my-4">
                            <hr />
                            <span className="position-absolute top-50 start-50 translate-middle bg-white px-2 text-muted small">
                              or
                            </span>
                          </div>

                          {!showGoogleTypeStep ? (
                            <div className="d-grid">
                              <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => dispatch(apiError("Google login failed"))}
                                text="continue_with"
                                theme="outline"
                                size="large"
                                width="100%"
                              />
                            </div>
                          ) : (
                            <div
                              className="rounded-3 p-3 border bg-light"
                              style={{ backgroundColor: "rgba(0,0,0,.02)", borderColor: "rgba(0,0,0,.08)" }}
                            >
                              <p className="text-muted small mb-3">
                                You&apos;re new here. Choose how you want to use SoldX.
                              </p>
                              <div className="mb-3">
                                <Label className="mb-1">I am a</Label>
                                <Input
                                  type="select"
                                  value={googleLoginType}
                                  onChange={(e) => setGoogleLoginType(e.target.value as "Buyer" | "Seller" | "Broker")}
                                  className="form-select border shadow-sm"
                                  style={{ borderRadius: "8px", padding: "0.5rem 0.75rem", fontWeight: 500 }}
                                >
                                  <option value="Buyer">Buyer</option>
                                  <option value="Seller">Seller</option>
                                  <option value="Broker">Broker</option>
                                </Input>
                              </div>
                              <div className="d-grid gap-2">
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  onClick={handleGoogleRoleContinue}
                                  disabled={loading}
                                >
                                  {loading ? "Please wait..." : "Continue"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm text-muted text-decoration-none p-0"
                                  onClick={() => {
                                    setShowGoogleTypeStep(false)
                                    setPendingGoogleCredential(null)
                                  }}
                                >
                                  <i className="mdi mdi-arrow-left me-1" />
                                  Back
                                </button>
                              </div>
                            </div>
                          )}

                          <Row className="form-group mb-0 mt-3">
                            <div className="col-12">
                              <Link to="/forgot-password" className="text-muted">
                                <i className="mdi mdi-lock" /> Forgot your password?
                              </Link>
                            </div>
                          </Row>
                        </Form>
                      </>
                    ) : (
                      <>
                        <h4 className="text-muted font-size-18 mb-1 text-center">Verify OTP</h4>
                        <p className="text-muted text-center">
                          We sent a 6-digit code to <strong>{savedEmail}</strong>. Enter it below.
                        </p>
                        <Form className="form-horizontal mt-4" onSubmit={handleVerifyOtp}>
                          {error ? <Alert color="danger">{error}</Alert> : null}
                          {otpError ? <Alert color="danger">{otpError}</Alert> : null}
                          <div className="mb-3">
                            <Label>Email</Label>
                            <Input type="email" value={savedEmail} readOnly disabled className="form-control bg-light" />
                          </div>
                          <div className="mb-3">
                            <Label className="d-block text-center mb-3">Enter 6-digit OTP</Label>
                            <div
                              className="d-flex justify-content-center gap-2 flex-wrap"
                              style={{ gap: "0.5rem" }}
                            >
                              {Array.from({ length: OTP_LENGTH }, (_, index) => (
                                <input
                                  key={index}
                                  ref={(el) => { otpInputRefs.current[index] = el }}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={1}
                                  value={otpDigits[index]?.trim() ?? ""}
                                  onChange={(e) => handleOtpChange(index, e.target.value)}
                                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                  onPaste={handleOtpPaste}
                                  autoComplete="one-time-code"
                                  className="form-control text-center fw-bold"
                                  style={{
                                    width: "2.75rem",
                                    height: "2.75rem",
                                    fontSize: "1.25rem",
                                    padding: "0.25rem",
                                    border: "2px solid #ced4da",
                                    borderRadius: "8px",
                                  }}
                                  aria-label={`Digit ${index + 1}`}
                                />
                              ))}
                            </div>
                          </div>
                          <Row className="mb-3 mt-4">
                            <div className="col-6">
                              <button
                                type="button"
                                className="btn btn-light w-md"
                                onClick={handleBackToEmailPassword}
                                disabled={loading}
                              >
                                Back
                              </button>
                            </div>
                            <div className="col-6 text-end">
                              <button
                                className="btn btn-primary w-md waves-effect waves-light"
                                type="submit"
                                disabled={loading}
                              >
                                {loading ? "Verifying..." : "Verify & Log In"}
                              </button>
                            </div>
                          </Row>
                        </Form>
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>

              <div className="mt-5 text-center">
                <p>
                  Don&apos;t have an account? <Link to="/register" className="text-primary">Signup Now</Link>
                </p>
                <p>
                  <Link to="/" className="btn btn-outline-primary btn-sm">
                    <i className="mdi mdi-home me-1"></i>Go to Home
                  </Link>
                </p>
                © {new Date().getFullYear()} SoldX <span className="d-none d-sm-inline-block"> - All Rights Reserved</span>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(Login)

Login.propTypes = {
  history: PropTypes.object,
}
